// Módulos de sensores en 3D: placas KY (Hall, termistor, sonido), placas LM393 (luz, llama),
// MQ-2, sensor de humedad de suelo con su horquilla y sensor de nivel de agua.
import {
  Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, PITCH, THREE,
  sgeo, sharedTex, sharedMat, flatBoard, pad, silkText, traces, headerRightAngle, headerDown, soic, smd, trimmer3362, trimmer3296,
  smdLed, lead, meshTexture, COLORS,
} from './sensors-kit.js';
import { KY, LM393, KY038, MQ2BOARD, SOIL, WATER } from '../../parts/sensors.js';

const pinZ = (n) => Array.from({ length: n }, (_, i) => (i - (n - 1) / 2) * PITCH);

/** Pads del conector y etiquetas de los pines junto al plástico. */
function headerSilk(g, X, Y, xEdge, labels, { size = 0.95, gap = 4.6 } = {}) {
  const zs = pinZ(labels.length);
  labels.forEach((l, i) => {
    pad(g, X(xEdge + 3.6), Y(zs[i]), 0.7, 0.32);
    if (l) silkText(g, l, X(xEdge + gap), Y(zs[i]), size, { align: 'left' });
  });
}

/** Encapsulado TO-92S del A3144 parado en el extremo de la placa (cara sensible hacia +X). */
function hallChip(b, x, top) {
  b.box(1.55, 3.0, 4.0, M.chip, x, top + 3.0, 0);
  for (const z of [-1.27, 0, 1.27]) lead(b, [[x, top + 3.1, z], [x, top, z]], 0.2);
}

function hallLabel() {
  return sharedTex('a3144-label', () => {
    const { c, g } = canvas(4, 3, 50);
    text(g, '3144', 2, 1.1, { size: 0.9, color: '#b9bbbe', weight: 600 });
    text(g, 'EUA', 2, 2.1, { size: 0.75, color: '#9c9ea2', weight: 600 });
    return texture(c);
  });
}

/** Imán de barra (polo N rojo, S azul) que aparece junto al sensor Hall. */
function magnet() {
  const g = new THREE.Group();
  const n = new THREE.Mesh(sgeo('magnet-half', () => new THREE.BoxGeometry(6, 4, 4)), mat('#d7362e', { roughness: 0.45 }));
  const s = new THREE.Mesh(sgeo('magnet-half', () => new THREE.BoxGeometry(6, 4, 4)), mat('#2c5fd1', { roughness: 0.45 }));
  n.position.x = -3;
  s.position.x = 3;
  n.castShadow = s.castShadow = true;
  const label = sharedTex('magnet-ns', () => {
    const { c, g: ctx } = canvas(12, 4, 30);
    text(ctx, 'N', 3, 2.1, { size: 2.6, color: '#ffffff', weight: 800 });
    text(ctx, 'S', 9, 2.1, { size: 2.6, color: '#ffffff', weight: 800 });
    return texture(c);
  });
  const top = new THREE.Mesh(new THREE.PlaneGeometry(12, 4).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: label, transparent: true, depthWrite: false }));
  top.position.y = 2.01;
  g.add(n, s, top);
  return g;
}

// KY-003 (Hall) -------------------------------------------------------------------------------------------
export function buildHall(def, props) {
  const b = new Builder();
  const xL = -KY.w / 2;
  flatBoard(b.root, {
    key: 'ky003', w: KY.w, d: KY.d, t: KY.t, y0: KY.y0, color: COLORS.pcbBlack,
    draw: (g, X, Y) => {
      traces(g, '#2a2c31', [[[X(xL + 3.6), Y(-2.54)], [X(2), Y(-2.54)], [X(6.8), Y(-1.27)]], [[X(xL + 3.6), Y(0)], [X(6.8), Y(0)]], [[X(xL + 3.6), Y(2.54)], [X(3), Y(4)], [X(6.8), Y(1.27)]]]);
      headerSilk(g, X, Y, xL, ['S', '', '−'], { size: 1.2 });
      silkText(g, 'KY-003', X(0.8), Y(-5.4), 1.25);
      for (const z of [-1.27, 0, 1.27]) pad(g, X(6.8), Y(z), 0.55, 0.25);
    },
  });
  headerRightAngle(b, { n: 3, xEdge: xL, top: KY.top, tipX: KY.pinX });
  hallChip(b, 6.8, KY.top);
  smd(b, 0.5, KY.top, 4.6);
  smd(b, 1.5, KY.top, -2.6, { kind: 'c' });
  b.build();
  const led = smdLed(b.root, -2.6, KY.top, 4.6, '#ff2a1e');
  const face = new THREE.Mesh(new THREE.PlaneGeometry(4, 3), new THREE.MeshBasicMaterial({ map: hallLabel(), transparent: true, depthWrite: false }));
  face.rotation.y = Math.PI / 2;
  face.position.set(6.8 + 0.79, KY.top + 4.5, 0);
  b.root.add(face);
  const mag = magnet();
  mag.position.set(15.5, KY.top + 2.6, 0);
  b.root.add(mag);
  const ctl = { kind: 'toggle', key: 'magnet', label: 'Acercar o retirar el imán' };
  b.root.traverse((o) => {
    if (o.isMesh && (o.parent === mag || o === face)) o.userData.interactive = ctl;
  });
  // La pastilla A3144 también responde al clic: es una caja invisible un poco más grande.
  const hit = new THREE.Mesh(sgeo('hall-hit', () => new THREE.BoxGeometry(6, 8, 7)), new THREE.MeshBasicMaterial({ visible: false }));
  hit.position.set(7.5, KY.top + 4, 0);
  hit.userData.interactive = ctl;
  b.root.add(hit);
  const set = (p) => {
    mag.visible = !!p.magnet;
  };
  set(props);
  return {
    root: b.root,
    setProps: set,
    update(v) {
      led.set(v.led ? 1 : 0);
    },
    reset() {
      led.set(0);
    },
  };
}

// KY-013 (termistor) --------------------------------------------------------------------------------------
export function buildNtcModule() {
  const b = new Builder();
  const xL = -KY.w / 2;
  flatBoard(b.root, {
    key: 'ky013', w: KY.w, d: KY.d, t: KY.t, y0: KY.y0, color: COLORS.pcbBlack,
    draw: (g, X, Y) => {
      traces(g, '#2a2c31', [[[X(xL + 3.6), Y(-2.54)], [X(1), Y(-2.54)], [X(6), Y(-1.27)]], [[X(xL + 3.6), Y(0)], [X(-1), Y(2.5)], [X(6), Y(1.27)]], [[X(xL + 3.6), Y(2.54)], [X(-2), Y(4.5)]]]);
      headerSilk(g, X, Y, xL, ['S', '', '−'], { size: 1.2 });
      silkText(g, 'KY-013', X(-1.2), Y(-5.4), 1.25);
      for (const z of [-1.27, 1.27]) pad(g, X(6), Y(z), 0.55, 0.25);
    },
  });
  headerRightAngle(b, { n: 3, xEdge: xL, top: KY.top, tipX: KY.pinX });
  const epoxy = mat('#121214', { roughness: 0.2 });
  b.add(sgeo('ntc-bead', () => new THREE.SphereGeometry(1.5, 20, 14)), epoxy, { x: 6, y: KY.top + 5.6, z: 0, sx: 0.82, sy: 1.35, sz: 1 });
  for (const s of [-1, 1]) lead(b, [[6, KY.top + 4.4, s * 0.45], [6, KY.top + 3.2, s * 0.45], [6, KY.top + 1.4, s * 1.27], [6, KY.top - 0.2, s * 1.27]], 0.2);
  smd(b, -1.5, KY.top, 3.8);
  b.build();
  return { root: b.root };
}

// KY-038 (sonido) ----------------------------------------------------------------------------------------
export function buildSound() {
  const K = KY038;
  const b = new Builder();
  const xL = -K.w / 2;
  flatBoard(b.root, {
    key: 'ky038', w: K.w, d: K.d, t: K.t, y0: K.y0, color: COLORS.pcbBlack,
    holes: [[-12.5, 0, 1.5]],
    draw: (g, X, Y) => {
      traces(g, '#2a2c31', [[[X(xL + 3.6), Y(-3.81)], [X(-2), Y(-5.5)], [X(13), Y(-3)]], [[X(xL + 3.6), Y(3.81)], [X(-10), Y(5.8)], [X(-6), Y(3)]], [[X(xL + 3.6), Y(1.27)], [X(-16), Y(4)], [X(8), Y(5.8)]]]);
      headerSilk(g, X, Y, xL, ['A0', 'G', '+', 'D0'], { size: 1.0 });
      silkText(g, 'KY-038', X(-6), Y(5.9), 1.1);
      silkText(g, 'L1', X(-13.6), Y(-6.3), 0.9);
      silkText(g, 'L2', X(-13.6), Y(6.3), 0.9);
      g.fillStyle = COLORS.pad;
      g.beginPath();
      g.arc(X(-12.5), Y(0), 2.4, 0, Math.PI * 2);
      g.fill();
    },
  });
  headerRightAngle(b, { n: 4, xEdge: xL, top: K.top, tipX: K.pinX });
  soic(b, -5.5, K.top, -1.5, 8, { rot: 1 });
  trimmer3296(b, 4.2, K.top, 3.0);
  for (const [x, z] of [[-9, -4.8], [-9, 1.5], [0, -5], [-2, -5.6], [8.5, -5]]) smd(b, x, K.top, z, { rot: x < 0 ? 1 : 0 });
  // Micrófono electret: lata metálica con fieltro negro arriba
  b.cyl(4.85, 4.85, 6.5, mat('#a7abb0', { roughness: 0.35, metalness: 0.8 }), 16.5, K.top, 0, 36);
  b.build();
  const felt = sharedMat('mic-felt', () => new THREE.MeshStandardMaterial({ map: meshTexture('#141416', '#2c2d30'), roughness: 0.95 }));
  const top = new THREE.Mesh(sgeo('mic-top', () => new THREE.CircleGeometry(4.3, 36).rotateX(-Math.PI / 2)), felt);
  top.position.set(16.5, K.top + 6.52, 0);
  const rim = new THREE.Mesh(sgeo('mic-rim', () => new THREE.RingGeometry(4.3, 4.85, 36).rotateX(-Math.PI / 2)), mat('#b9bcc1', { roughness: 0.3, metalness: 0.85 }));
  rim.position.set(16.5, K.top + 6.51, 0);
  const hit = new THREE.Mesh(sgeo('mic-hit', () => new THREE.CylinderGeometry(5.2, 5.2, 7, 16)), new THREE.MeshBasicMaterial({ visible: false }));
  hit.position.set(16.5, K.top + 3.3, 0);
  hit.userData.interactive = top.userData.interactive = { kind: 'press', key: 'loud', label: 'Hacer ruido fuerte (mantener)' };
  b.root.add(top, rim, hit);
  const l1 = smdLed(b.root, -13.6, K.top, -4.3, '#ff2a1e');
  const l2 = smdLed(b.root, -13.6, K.top, 4.3, '#ff2a1e');
  const press = (down) => {
    top.position.y = K.top + 6.52 - (down ? 0.25 : 0);
  };
  return {
    root: b.root,
    update(v) {
      l1.set(v.on ? 1 : 0);
      l2.set(v.led || 0);
    },
    press(key, down) {
      press(down);
    },
    reset() {
      l1.set(0);
      l2.set(0);
      press(false);
    },
  };
}

// Placas LM393 de 32 × 14 mm (luz y llama) ---------------------------------------------------------------
function lm393Board(b, key) {
  const L = LM393;
  const xL = -L.w / 2;
  flatBoard(b.root, {
    key, w: L.w, d: L.d, t: L.t, y0: L.y0, color: COLORS.pcbBlue,
    draw: (g, X, Y) => {
      traces(g, 'rgba(78,128,210,0.6)', [[[X(xL + 3.6), Y(-3.81)], [X(-9), Y(-6)], [X(9), Y(-6)], [X(13.4), Y(-1.27)]], [[X(xL + 3.6), Y(3.81)], [X(-6), Y(6)], [X(3), Y(6)]], [[X(xL + 3.6), Y(1.27)], [X(-3), Y(2.6)]]]);
      headerSilk(g, X, Y, xL, ['VCC', 'GND', 'DO', 'AO'], { size: 0.85 });
      silkText(g, 'PWR', X(-6.8), Y(-6.1), 0.75);
      silkText(g, 'DO', X(-6.8), Y(6.1), 0.75);
      for (const z of [-1.27, 1.27]) pad(g, X(13.4), Y(z), 0.55, 0.25);
    },
  });
  headerRightAngle(b, { n: 4, xEdge: xL, top: L.top, tipX: L.pinX });
  soic(b, -1.5, L.top, 0, 8, { rot: 1 });
  trimmer3362(b, 6.6, L.top, 0, { color: '#2a63c8', rotor: '#f2f0e8', angle: 0.6 });
  for (const [x, z] of [[-6.8, -1.8], [-6.8, 1.8], [2.4, -5.4], [2.4, 5.4], [10.5, 4.6]]) smd(b, x, L.top, z, { rot: 1 });
}

function lm393Leds(b) {
  const L = LM393;
  return [smdLed(b.root, -9.6, L.top, -4.4, '#ff2a1e'), smdLed(b.root, -9.6, L.top, 4.4, '#2bff5a')];
}

function moduleView(b, [pwr, dled]) {
  return {
    root: b.root,
    update(v) {
      pwr.set(v.on ? 1 : 0);
      dled.set(v.led || 0);
    },
    reset() {
      pwr.set(0);
      dled.set(0);
    },
  };
}

function ldrTopMat() {
  return sharedMat('ldr-top-mod', () => new THREE.MeshStandardMaterial({ map: sharedTex('ldr-top-mod', () => {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g = c.getContext('2d');
    g.fillStyle = '#ece4d2';
    g.fillRect(0, 0, 128, 128);
    g.fillStyle = '#a3a19b';
    g.fillRect(14, 20, 100, 15);
    g.fillRect(14, 93, 100, 15);
    g.strokeStyle = '#c4462a';
    g.lineWidth = 5;
    g.beginPath();
    let y = 36;
    g.moveTo(20, y);
    for (let i = 0; i < 7; i++) {
      const x = i % 2 ? 20 : 108;
      g.lineTo(x, y);
      y += 8;
      g.lineTo(x, y);
    }
    g.stroke();
    return texture(c);
  }), roughness: 0.18 }));
}

export function buildLightModule() {
  const b = new Builder();
  lm393Board(b, 'mh-light');
  const top = LM393.top;
  for (const s of [-1, 1]) lead(b, [[13.4, top + 2.9, s * 0.95], [13.4, top + 1.6, s * 1.27], [13.4, top - 0.2, s * 1.27]], 0.2);
  b.build();
  const disc = new THREE.Mesh(sgeo('ldr-disc', () => new THREE.CylinderGeometry(2.55, 2.55, 1.9, 32)), [mat('#d8cfb9', { roughness: 0.22 }), ldrTopMat(), mat('#d8cfb9', { roughness: 0.22 })]);
  disc.position.set(13.4, top + 3.8, 0);
  disc.castShadow = true;
  b.root.add(disc);
  return moduleView(b, lm393Leds(b));
}

export function buildFlame() {
  const b = new Builder();
  lm393Board(b, 'mh-flame');
  const top = LM393.top;
  for (const s of [-1, 1]) lead(b, [[13.4, top + 1.6, s * 1.27], [13.4, top - 0.2, s * 1.27]], 0.22);
  b.build();
  // Receptor IR de 5 mm: cuerpo de epoxi negro azulado con cúpula
  const ir = new THREE.MeshStandardMaterial({ color: '#141722', roughness: 0.12, metalness: 0.1, transparent: true, opacity: 0.94 });
  const body = new THREE.Mesh(sgeo('ir-body', () => {
    const pts = [new THREE.Vector2(0, 0), new THREE.Vector2(2.9, 0), new THREE.Vector2(2.9, 1.0), new THREE.Vector2(2.5, 1.0), new THREE.Vector2(2.5, 5.4)];
    for (let i = 1; i <= 10; i++) {
      const a = (i / 10) * (Math.PI / 2);
      pts.push(new THREE.Vector2(2.5 * Math.cos(a), 5.4 + 2.5 * Math.sin(a)));
    }
    return new THREE.LatheGeometry(pts, 28);
  }), ir);
  body.position.set(13.4, top + 1.6, 0);
  body.castShadow = true;
  b.root.add(body);
  return moduleView(b, lm393Leds(b));
}

// MQ-2 ------------------------------------------------------------------------------------------------
export function buildGas() {
  const Q = MQ2BOARD;
  const b = new Builder();
  const sz = Q.sensorZ;
  const pcb = extrudeBoard(boardShape(Q.w, Q.d, { r: 0.8 }), Q.t, null, COLORS.pcbBlue, { holes: [[-13, 0, 1.4], [13, 0, 1.4]], w: Q.w, d: Q.d, y0: Q.pcbY });
  pcb.position.z = Q.zc;
  b.root.add(pcb);
  const tex = sharedTex('mq2-top', () => {
    const { c, g } = canvas(Q.w, Q.d, 18);
    const X = (x) => x + Q.w / 2;
    const Y = (z) => z - Q.zc + Q.d / 2;
    g.fillStyle = COLORS.pcbBlue;
    g.fillRect(0, 0, Q.w, Q.d);
    g.strokeStyle = COLORS.silk;
    g.lineWidth = 0.22;
    g.beginPath();
    g.arc(X(0), Y(sz), 10.6, 0, Math.PI * 2);
    g.stroke();
    ['VCC', 'GND', 'DO', 'AO'].forEach((l, i) => {
      const x = (i - 1.5) * PITCH;
      pad(g, X(x), Y(0), 0.75, 0.35);
      silkText(g, l, X(x), Y(1.55), 0.72);
    });
    silkText(g, 'MQ-2', X(-12.6), Y(-17), 1.3);
    silkText(g, 'PWR', X(-12.8), Y(-3.4), 0.8);
    silkText(g, 'DO', X(12.8), Y(-3.4), 0.8);
    for (const s of [-1, 1]) {
      g.fillStyle = COLORS.pad;
      g.beginPath();
      g.arc(X(s * 13), Y(Q.zc), 2.2, 0, Math.PI * 2);
      g.fill();
    }
    g.globalCompositeOperation = 'destination-out';
    for (const s of [-1, 1]) {
      g.beginPath();
      g.arc(X(s * 13), Y(Q.zc), 1.4, 0, Math.PI * 2);
      g.fill();
    }
    return texture(c);
  });
  const silk = new THREE.Mesh(new THREE.PlaneGeometry(Q.w, Q.d).rotateX(-Math.PI / 2), sharedMat('mq2-top', () => new THREE.MeshStandardMaterial({ map: tex, alphaTest: 0.5, roughness: 0.5 })));
  silk.position.set(0, Q.top + 0.02, Q.zc);
  b.root.add(silk);
  headerDown(b, { xs: [-1.5, -0.5, 0.5, 1.5].map((k) => k * PITCH), z: 0, bottom: Q.pcbY, t: Q.t });
  // Sensor: base de baquelita, pestaña engargolada y doble malla de acero inoxidable
  b.cyl(9.6, 10, 4.2, mat('#2a2522', { roughness: 0.55 }), 0, Q.top, sz, 40);
  b.cyl(10.3, 10.3, 0.8, M.steel, 0, Q.top + 4.2, sz, 40);
  b.build();
  const meshMat = sharedMat('mq2-mesh', () => {
    const t = meshTexture('#7c8187', '#c9ccd0').clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(10, 2);
    t.userData.shared = true;
    return new THREE.MeshStandardMaterial({ map: t, roughness: 0.4, metalness: 0.75 });
  });
  const cap = new THREE.Mesh(sgeo('mq2-cap', () => new THREE.CylinderGeometry(8.4, 8.6, 9.6, 40)), [meshMat, sharedMat('mq2-cap-top', () => new THREE.MeshStandardMaterial({ map: meshTexture('#7c8187', '#c9ccd0'), roughness: 0.4, metalness: 0.75 })), M.steel]);
  cap.position.set(0, Q.top + 5.0 + 4.8, sz);
  cap.castShadow = true;
  b.root.add(cap);
  // Debajo: perilla del umbral, LM393 y resistencias
  const under = new Builder();
  trimmer3362(under, -9, Q.pcbY, -16.5, { under: true, angle: 0.3 });
  soic(under, 7, Q.pcbY, -16, 8, { under: true });
  for (const [x, z] of [[-4, -5], [4, -5], [10, -9], [-10, -9]]) smd(under, x, Q.pcbY, z, { under: true });
  under.build();
  b.root.add(under.root);
  const leds = [smdLed(b.root, -12.8, Q.top, -1.4, '#ff2a1e'), smdLed(b.root, 12.8, Q.top, -1.4, '#ff2a1e')];
  return moduleView(b, leds);
}

// Humedad de suelo: comparador + horquilla ----------------------------------------------------------------
function probeShape() {
  // (x, z) de la horquilla; en la forma 2D y = −z.
  const W = SOIL.probeW;
  const pts = [[0, -10], [W - 4, -10], [W, -6.75], [W - 4, -3.5], [W - SOIL.prongs, -3.5], [W - SOIL.prongs, 3.5], [W - 4, 3.5], [W, 6.75], [W - 4, 10], [0, 10]];
  const s = new THREE.Shape();
  s.moveTo(pts[0][0], -pts[0][1]);
  for (const [x, z] of pts.slice(1)) s.lineTo(x, -z);
  s.lineTo(pts[0][0], -pts[0][1]);
  return { s, pts };
}

function probeTexture() {
  return sharedTex('soil-probe', () => {
    const W = SOIL.probeW;
    const D = SOIL.probeD;
    const { c, g } = canvas(W, D, 14);
    const Y = (z) => z + D / 2;
    const { pts } = probeShape();
    g.fillStyle = COLORS.pcbBlue;
    g.beginPath();
    g.moveTo(pts[0][0], Y(pts[0][1]));
    for (const [x, z] of pts.slice(1)) g.lineTo(x, Y(z));
    g.closePath();
    g.fill();
    // Pistas expuestas (niqueladas) a lo largo de cada punta
    g.fillStyle = '#c8cbcf';
    for (const s of [-1, 1]) {
      g.beginPath();
      g.moveTo(9, Y(s * 1.27) - 0.6);
      g.lineTo(14, Y(s * 6.75) - 0.6);
      g.lineTo(W - 5.5, Y(s * 6.75) - 1.9);
      g.lineTo(W - 2.2, Y(s * 6.75));
      g.lineTo(W - 5.5, Y(s * 6.75) + 1.9);
      g.lineTo(14, Y(s * 6.75) + 0.6);
      g.lineTo(9, Y(s * 1.27) + 0.6);
      g.closePath();
      g.fill();
      pad(g, 3.6, Y(s * 1.27), 0.75, 0.32);
    }
    return texture(c);
  });
}

export function buildSoil() {
  const S = SOIL;
  const b = new Builder();
  const xL = -S.w / 2;
  flatBoard(b.root, {
    key: 'soil-board', w: S.w, d: S.d, t: S.t, y0: S.y0, color: COLORS.pcbBlue,
    draw: (g, X, Y) => {
      traces(g, 'rgba(78,128,210,0.6)', [[[X(xL + 3.6), Y(-3.81)], [X(-6), Y(-6.5)], [X(10), Y(-6.5)], [X(11.4), Y(-1.27)]], [[X(xL + 3.6), Y(3.81)], [X(-4), Y(6.5)], [X(11.4), Y(1.27)]]]);
      headerSilk(g, X, Y, xL, ['VCC', 'GND', 'DO', 'AO'], { size: 0.85 });
      for (const z of [-1.27, 1.27]) pad(g, X(S.w / 2 - 3.6), Y(z), 0.7, 0.32);
      silkText(g, 'PWR', X(-6.6), Y(-6.6), 0.75);
      silkText(g, 'DO', X(-6.6), Y(6.6), 0.75);
    },
  });
  headerRightAngle(b, { n: 4, xEdge: xL, top: S.top, tipX: S.pinX });
  headerRightAngle(b, { n: 2, xEdge: S.w / 2, top: S.top, tipX: S.w / 2 + S.pinLen, dir: 1 });
  soic(b, -1, S.top, 0, 8, { rot: 1 });
  trimmer3362(b, 6.2, S.top, 0, { color: '#2a63c8', rotor: '#f2f0e8', angle: -0.4 });
  for (const [x, z] of [[-7, -1.8], [-7, 1.8], [2.5, -5.6], [2.5, 5.6]]) smd(b, x, S.top, z, { rot: 1 });
  // Horquilla y su conector
  const px = S.probeX0;
  const probe = extrudeBoard(probeShape().s, S.t, null, COLORS.pcbBlue, { w: S.probeW, d: S.probeD, y0: S.y0 });
  probe.position.x = px;
  b.root.add(probe);
  const ptex = probeTexture();
  const ptop = new THREE.Mesh(new THREE.PlaneGeometry(S.probeW, S.probeD).rotateX(-Math.PI / 2), sharedMat('soil-probe', () => new THREE.MeshStandardMaterial({ map: ptex, alphaTest: 0.5, roughness: 0.35, metalness: 0.2 })));
  ptop.position.set(px + S.probeW / 2, S.top + 0.02, 0);
  b.root.add(ptop);
  headerRightAngle(b, { n: 2, xEdge: px, top: S.top, tipX: px - S.pinLen });
  // Dos cables hembra-hembra entre la placa y la horquilla
  const y = S.top + 1.27;
  const a0 = S.w / 2 + 0.3;
  const a1 = a0 + 14;
  const b1 = px - 0.3;
  const b0 = b1 - 14;
  [['#d23a2c', -PITCH / 2], ['#2b2b2e', PITCH / 2]].forEach(([color, z]) => {
    b.box(14, 2.5, 2.5, M.blackPlastic, (a0 + a1) / 2, y - 1.25, z);
    b.box(14, 2.5, 2.5, M.blackPlastic, (b0 + b1) / 2, y - 1.25, z);
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(a1, y, z), new THREE.Vector3(a1 + 4, y - 0.8, z * 2.2), new THREE.Vector3(b0 - 4, y - 0.8, z * 2.2), new THREE.Vector3(b0, y, z),
    ]);
    b.add(new THREE.TubeGeometry(curve, 24, 0.7, 8, false), mat(color, { roughness: 0.45 }));
  });
  b.build();
  return moduleView(b, [smdLed(b.root, -9.4, S.top, -4.6, '#ff2a1e'), smdLed(b.root, -9.4, S.top, 4.6, '#2bff5a')]);
}

// Nivel de agua -------------------------------------------------------------------------------------------
export function buildWater(def, props) {
  const Wt = WATER;
  const b = new Builder();
  const xL = -Wt.w / 2;
  const x0 = Wt.traceX0;
  const x1 = Wt.traceX0 + Wt.traceLen;
  flatBoard(b.root, {
    key: 'water', w: Wt.w, d: Wt.d, t: Wt.t, y0: Wt.y0, color: COLORS.pcbRed, r: 1.2,
    holes: [[xL + 12.5, -6.6, 1.4]],
    draw: (g, X, Y) => {
      headerSilk(g, X, Y, xL, ['S', '+', '−'], { size: 1.3 });
      // Diez pistas expuestas entrelazadas: cinco van a +V y cinco a la base del transistor.
      g.fillStyle = '#d3d5d8';
      for (let k = 0; k < 10; k++) {
        const z = -8.1 + k * 1.8;
        const start = k % 2 ? x0 + 1.4 : x0;
        g.fillRect(X(start), Y(z) - 0.45, x1 - start, 0.9);
      }
      g.fillRect(X(x0), Y(-8.1) - 0.45, 0.9, 16.2);
      g.fillRect(X(x0 + 1.4), Y(-6.3) - 0.45, 0.9, 14.4);
      g.strokeStyle = 'rgba(232,110,100,0.7)';
      g.lineWidth = 0.4;
      g.beginPath();
      g.moveTo(X(xL + 3.6), Y(0));
      g.lineTo(X(x0), Y(0));
      g.stroke();
      g.fillStyle = COLORS.pad;
      g.beginPath();
      g.arc(X(xL + 12.5), Y(-6.6), 2.2, 0, Math.PI * 2);
      g.fill();
    },
  });
  headerRightAngle(b, { n: 3, xEdge: xL, top: Wt.top, tipX: Wt.pinX });
  b.box(2.9, 1.0, 1.3, M.chip, -22.5, Wt.top, 4.6);
  for (const dz of [-0.95, 0.95]) b.box(0.4, 0.5, 0.5, M.tin, -23.4, Wt.top, 4.6 + dz);
  b.box(0.4, 0.5, 0.5, M.tin, -21.6, Wt.top, 4.6);
  for (const [x, z] of [[-18.5, 4.4], [-15.5, -4.4], [-15.5, 0.6]]) smd(b, x, Wt.top, z, { rot: 1 });
  b.build();
  const pwr = smdLed(b.root, -22.5, Wt.top, -4.6, '#ff2a1e');
  // Agua sobre la parte sumergida (desde el extremo de las pistas)
  const waterMat = new THREE.MeshStandardMaterial({ color: '#3d9be0', transparent: true, opacity: 0.4, roughness: 0.08, metalness: 0.1, depthWrite: false });
  const water = new THREE.Mesh(sgeo('water-box', () => new THREE.BoxGeometry(1, 1, 1)), waterMat);
  water.userData.noPick = true;
  const line = new THREE.Mesh(sgeo('water-line', () => new THREE.BoxGeometry(0.35, 0.06, 1)), new THREE.MeshBasicMaterial({ color: '#d9f1ff', transparent: true, opacity: 0.85 }));
  line.userData.noPick = true;
  b.root.add(water, line);
  const set = (p) => {
    const level = Math.max(0, Math.min(Wt.maxLevel, Number(p.level) || 0));
    water.visible = line.visible = level > 0;
    const from = x1 - level;
    const to = Wt.w / 2 + 0.4;
    water.scale.set(to - from, 1.1, Wt.d + 0.8);
    water.position.set((from + to) / 2, Wt.top + 0.55, 0);
    line.scale.set(1, 1, Wt.d + 0.8);
    line.position.set(from, Wt.top + 1.12, 0);
  };
  set(props);
  return {
    root: b.root,
    setProps: set,
    update(v) {
      pwr.set(v.on ? 1 : 0);
    },
    reset() {
      pwr.set(0);
    },
  };
}

export const MODULE_MODELS = {
  'hall-sensor': buildHall,
  'ntc-module': buildNtcModule,
  'sound-sensor': buildSound,
  'photoresistor-module': buildLightModule,
  'flame-sensor': buildFlame,
  'gas-sensor': buildGas,
  'soil-moisture': buildSoil,
  'water-level': buildWater,
};
